import React from 'react';

import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuTypePropType } from 'ui-components/components/dropdown-menu/dropdown-menu-type';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import { InsuranceProviders } from '../../enums/insurance-providers';
import { useEnableInsurancePromoExperiment } from '../../feature-flags/hooks/use-enable-insurance-promo-experiment';
import { MessageDescriptorPropType } from '../../prop-types/intl';
import { INSURANCE_FIELD_CLICKED_CSW, INSURANCE_FIELD_CHANGED_CSW } from '../../constants/analytics-events';
import { useInsurancePreference, INSURANCE_PREFERENCE } from '../../hooks/use-insurance-preference';
import { Segment } from '../../services/analytics-services/segment';
import messages from '../messages';
import { useFulfillmentPlannerContext } from '../context';

const getFormattedMessageContent = (messageDescriptor, insurancePromoEnabledMessageDescriptor, isInsurancePromoExperimentEnabled) => {
  if (isInsurancePromoExperimentEnabled && insurancePromoEnabledMessageDescriptor) {
    return insurancePromoEnabledMessageDescriptor;
  }
  return messageDescriptor;
};

const InsuranceOptionDropdown = ({
  id,
  dropdownMenuType,
  insuranceOptions,
  selectedInsuranceOptionName,
  onChange,
  className = null,
  isPartOfInsurancePromoExperiment = false,
}) => {
  const intl = useIntl();
  const isInsurancePromoExperimentEnabled = useEnableInsurancePromoExperiment();
  const { insurancePreference, hasExplicitNonePreference, insurancePreferenceForAnalytics } = useInsurancePreference();
  const selectedInsuranceOption =
    insuranceOptions.find((insuranceOption) => insuranceOption.name === selectedInsuranceOptionName) || InsuranceProviders.None;
  const selectedInsuranceOptionFormattedName = intl.formatMessage(
    getFormattedMessageContent(
      selectedInsuranceOption.messageDescriptor,
      selectedInsuranceOption.insurancePromoEnabledMessageDescriptor,
      isInsurancePromoExperimentEnabled,
    ),
  );

  const { isInsuranceOptionDirty, setIsInsuranceOptionDirty } = useFulfillmentPlannerContext();

  const isSkip = insurancePreference === INSURANCE_PREFERENCE.NONE && !hasExplicitNonePreference;

  const togglerText =
    isPartOfInsurancePromoExperiment && isSkip && !isInsuranceOptionDirty && selectedInsuranceOptionName === InsuranceProviders.None.name
      ? intl.formatMessage(messages.noInsuranceOptionSelected)
      : selectedInsuranceOptionFormattedName;

  return (
    <DropdownMenu
      ariaLabel={intl.formatMessage(messages.insurance)}
      className={className}
      dropdownMenuShouldHaveWidthOfButton
      dropdownMenuType={dropdownMenuType}
      fullWidth
      id={id}
      onOpen={() =>
        Segment.trackEvent(INSURANCE_FIELD_CLICKED_CSW, {
          version: isInsurancePromoExperimentEnabled ? 'v1' : 'v0',
          insurancePreference: insurancePreferenceForAnalytics,
        })
      }
      value={selectedInsuranceOptionName}
      variant='form'
    >
      <DropdownMenuToggler>{togglerText}</DropdownMenuToggler>
      {insuranceOptions.map(({ name, messageDescriptor, insurancePromoEnabledMessageDescriptor }) => (
        <DropdownMenuItem
          key={name}
          onClick={() => {
            setIsInsuranceOptionDirty(true);
            if (name !== selectedInsuranceOptionName) {
              Segment.trackEvent(INSURANCE_FIELD_CHANGED_CSW, {
                fromOption: selectedInsuranceOptionName,
                toOption: name,
                version: isInsurancePromoExperimentEnabled ? 'v1' : 'v0',
                insurancePreference: insurancePreferenceForAnalytics,
              });
            }
            onChange(name);
          }}
          selected={selectedInsuranceOptionName === name}
        >
          {intl.formatMessage(
            getFormattedMessageContent(messageDescriptor, insurancePromoEnabledMessageDescriptor, isInsurancePromoExperimentEnabled),
          )}
        </DropdownMenuItem>
      ))}
    </DropdownMenu>
  );
};

InsuranceOptionDropdown.propTypes = {
  id: PropTypes.string,
  dropdownMenuType: DropdownMenuTypePropType,
  insuranceOptions: PropTypes.arrayOf(
    PropTypes.shape({
      insuranceOptionId: PropTypes.string.isRequired,
      messageDescriptor: MessageDescriptorPropType,
    }),
  ).isRequired,
  selectedInsuranceOptionName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  isPartOfInsurancePromoExperiment: PropTypes.bool,
};

export default InsuranceOptionDropdown;
